import { usePwaInstallState } from "./usePwaInstallState";

// Persistent "Install MedoPilot" entry point for the marketing navigation and footer -- a
// separate, user-initiated installation path from the automatic guidance card. It never opens or
// otherwise touches that card; it only reads the same shared capability/prompt state
// (usePwaInstallState) so install logic is never duplicated between the two. Stays in the same
// position whether or not MedoPilot is installed, so nav/footer layout never shifts.
export function InstallMedoPilotEntry({ className, onClick }: { className: string; onClick?: () => void }) {
  const {
    isInstallable,
    isStandalone,
    canPromptInstall,
    requestInstall,
    openIosInstructions,
    openAlreadyInstalledNotice,
  } = usePwaInstallState();

  // Hidden only when this platform gives us no way to act on a click at all -- not merely because
  // MedoPilot happens to already be installed (see the standalone branch below).
  if (!isInstallable && !isStandalone) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => {
        if (isStandalone) {
          // Standalone display mode is the only "installed" signal this app can reliably detect,
          // so this never attempts to trigger installation again -- just a plain acknowledgement.
          openAlreadyInstalledNotice();
        } else if (canPromptInstall) {
          // Directly invokes the already-captured browser install prompt -- prompt() only ever
          // runs as this direct result of the click, never proactively and never via the card.
          void requestInstall();
        } else {
          // No programmatic install API on this platform (iOS/iPadOS): show accurate instructions
          // instead of pretending to install anything.
          openIosInstructions();
        }
        onClick?.();
      }}
      className={className}
    >
      Install MedoPilot
    </button>
  );
}
